import React, { useState, useEffect } from 'react'
import api from '../services/api'
import Modal from '../components/Modal'
import FlowchartEditor from '../components/FlowchartEditor'

interface Automation {
  id: number
  name: string
  description: string
  trigger_type: string
  trigger_config: Record<string, any>
  action_type: string
  action_config: Record<string, any>
  status: 'active' | 'paused' | 'disabled'
  priority: number
  created_at: string
  updated_at: string
}

interface AutomationLog {
  id: number
  automation: number
  automation_name: string
  trigger_data: Record<string, any>
  action_data: Record<string, any>
  status: 'success' | 'failed' | 'pending'
  error_message: string
  created_at: string
}

// Icons
const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
)

const FlowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="6" height="6" rx="1"></rect>
    <rect x="15" y="3" width="6" height="6" rx="1"></rect>
    <rect x="9" y="15" width="6" height="6" rx="1"></rect>
    <path d="M6 9v3a1 1 0 0 0 1 1h4"></path>
    <path d="M18 9v3a1 1 0 0 1-1 1h-4"></path>
  </svg>
)

const PlayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3"></polygon>
  </svg>
)

const PauseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="4" width="4" height="16"></rect>
    <rect x="14" y="4" width="4" height="16"></rect>
  </svg>
)

const LogsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
)

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
)

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
)

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
)

const ZapIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
  </svg>
)

const ArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
)

const TRIGGERS = [
  { value: 'lead_created', label: 'Lead Criado', icon: '👥', description: 'Dispara quando um novo lead é criado' },
  { value: 'lead_status_changed', label: 'Status do Lead Mudou', icon: '🔄', description: 'Dispara quando o status de um lead é alterado' },
  { value: 'deal_created', label: 'Negócio Criado', icon: '💼', description: 'Dispara quando um novo negócio/opportunity é criado' },
  { value: 'deal_stage_changed', label: 'Estágio do Negócio Mudou', icon: '📊', description: 'Dispara quando um negócio é movido para outro estágio' },
  { value: 'deal_status_changed', label: 'Status do Negócio Mudou', icon: '✨', description: 'Dispara quando o status de um negócio é alterado' },
  { value: 'message_received', label: 'Mensagem Recebida', icon: '💬', description: 'Dispara quando uma nova mensagem é recebida' },
  { value: 'schedule', label: 'Agendamento', icon: '⏰', description: 'Dispara em um horário específico ou recorrente' },
]

const ACTIONS = [
  { value: 'send_message', label: 'Enviar Mensagem', icon: '📱', description: 'Envia uma mensagem via WhatsApp ou outro canal' },
  { value: 'send_email', label: 'Enviar Email', icon: '📧', description: 'Envia um email para o lead ou contato' },
  { value: 'update_field', label: 'Atualizar Campo', icon: '✏️', description: 'Atualiza um campo do lead ou negócio' },
  { value: 'create_task', label: 'Criar Tarefa', icon: '✅', description: 'Cria uma nova tarefa ou atividade' },
  { value: 'assign_owner', label: 'Atribuir Responsável', icon: '👤', description: 'Atribui um atendente responsável' },
  { value: 'call_webhook', label: 'Chamar Webhook', icon: '🔗', description: 'Faz uma chamada HTTP para uma URL externa' },
  { value: 'move_stage', label: 'Mover Estágio', icon: '→', description: 'Move o negócio para outro estágio do pipeline' },
]

const Automations = () => {
  const [automations, setAutomations] = useState<Automation[]>([])
  const [logs, setLogs] = useState<AutomationLog[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showLogsModal, setShowLogsModal] = useState(false)
  const [showFlowEditor, setShowFlowEditor] = useState(false)
  const [selectedAutomation, setSelectedAutomation] = useState<Automation | null>(null)
  const [editingAutomation, setEditingAutomation] = useState<Partial<Automation>>({
    name: '',
    description: '',
    trigger_type: 'lead_created',
    trigger_config: {},
    action_type: 'send_message',
    action_config: {},
    status: 'active',
    priority: 0,
  })

  useEffect(() => {
    loadAutomations()
  }, [])

  const loadAutomations = async () => {
    try {
      setLoading(true)
      const response = await api.get('/automations/')
      const data = Array.isArray(response.data.results) ? response.data.results : Array.isArray(response.data) ? response.data : []
      setAutomations(data)
    } catch (error) {
      console.error('Error loading automations:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadLogs = async (automationId: number) => {
    try {
      const response = await api.get(`/automations/logs/?automation=${automationId}`)
      const data = Array.isArray(response.data.results) ? response.data.results : Array.isArray(response.data) ? response.data : []
      setLogs(data)
      setShowLogsModal(true)
    } catch (error) {
      console.error('Error loading logs:', error)
    }
  }

  const handleCreateAutomation = async () => {
    try {
      await api.post('/automations/', editingAutomation)
      setShowModal(false)
      setEditingAutomation({
        name: '',
        description: '',
        trigger_type: 'lead_created',
        trigger_config: {},
        action_type: 'send_message',
        action_config: {},
        status: 'active',
        priority: 0,
      })
      loadAutomations()
    } catch (error) {
      console.error('Error creating automation:', error)
    }
  }

  const handleUpdateAutomation = async () => {
    if (!selectedAutomation) return
    try {
      await api.put(`/automations/${selectedAutomation.id}/`, editingAutomation)
      setShowModal(false)
      setSelectedAutomation(null)
      setEditingAutomation({
        name: '',
        description: '',
        trigger_type: 'lead_created',
        trigger_config: {},
        action_type: 'send_message',
        action_config: {},
        status: 'active',
        priority: 0,
      })
      loadAutomations()
    } catch (error) {
      console.error('Error updating automation:', error)
    }
  }

  const handleDeleteAutomation = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta automação?')) return
    try {
      await api.delete(`/automations/${id}/`)
      loadAutomations()
    } catch (error) {
      console.error('Error deleting automation:', error)
    }
  }

  const handleToggleStatus = async (automation: Automation) => {
    try {
      const newStatus = automation.status === 'active' ? 'paused' : 'active'
      await api.patch(`/automations/${automation.id}/`, { status: newStatus })
      loadAutomations()
    } catch (error) {
      console.error('Error toggling status:', error)
    }
  }

  const openCreateModal = () => {
    setSelectedAutomation(null)
    setEditingAutomation({
      name: '',
      description: '',
      trigger_type: 'lead_created',
      trigger_config: {},
      action_type: 'send_message',
      action_config: {},
      status: 'active',
      priority: 0,
    })
    setShowModal(true)
  }

  const openEditModal = (automation: Automation) => {
    setSelectedAutomation(automation)
    setEditingAutomation(automation)
    setShowModal(true)
  }

  const getTriggerIcon = (type: string) => {
    const trigger = TRIGGERS.find(t => t.value === type)
    return trigger?.icon || '⚡'
  }

  const getActionIcon = (type: string) => {
    const action = ACTIONS.find(a => a.value === type)
    return action?.icon || '🎯'
  }

  const getTriggerLabel = (type: string) => {
    const trigger = TRIGGERS.find(t => t.value === type)
    return trigger?.label || type
  }

  const getActionLabel = (type: string) => {
    const action = ACTIONS.find(a => a.value === type)
    return action?.label || type
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return { bg: 'rgba(34, 197, 94, 0.1)', text: 'var(--color-success-600)', label: 'Ativo' }
      case 'paused': return { bg: 'rgba(245, 158, 11, 0.1)', text: 'var(--color-warning-600)', label: 'Pausado' }
      case 'disabled': return { bg: 'rgba(239, 68, 68, 0.1)', text: 'var(--color-error-600)', label: 'Desabilitado' }
      default: return { bg: 'var(--bg-tertiary)', text: 'var(--text-secondary)', label: status }
    }
  }

  const stats = [
    {
      label: 'Total',
      value: automations.length,
      icon: <ZapIcon />,
      color: 'var(--color-primary-500)',
      bgColor: 'rgba(99, 102, 241, 0.1)'
    },
    {
      label: 'Ativas',
      value: automations.filter(a => a.status === 'active').length,
      icon: <PlayIcon />,
      color: 'var(--color-success-500)',
      bgColor: 'rgba(34, 197, 94, 0.1)'
    },
    {
      label: 'Pausadas',
      value: automations.filter(a => a.status === 'paused').length,
      icon: <PauseIcon />,
      color: 'var(--color-warning-500)',
      bgColor: 'rgba(245, 158, 11, 0.1)'
    }
  ]

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: '1600px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-4)' }}>
          <div>
            <h1 style={{
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-2)'
            }}>
              Automações
            </h1>
            <p style={{ fontSize: 'var(--text-base)', color: 'var(--text-secondary)' }}>
              Crie fluxos de automação para otimizar seus processos
            </p>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <button
              onClick={() => setShowFlowEditor(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-3) var(--space-5)',
                background: 'var(--glass-bg)',
                backdropFilter: 'var(--glass-blur)',
                border: '2px solid var(--color-primary-500)',
                borderRadius: 'var(--radius-lg)',
                color: 'var(--color-primary-600)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
                transition: 'all var(--transition-fast)'
              }}
            >
              <FlowIcon />
              Editor Visual
            </button>
            <button
              onClick={openCreateModal}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-3) var(--space-5)',
                background: 'var(--gradient-primary)',
                border: 'none',
                borderRadius: 'var(--radius-lg)',
                color: 'white',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
                boxShadow: 'var(--shadow-md)',
                transition: 'all var(--transition-fast)'
              }}
            >
              <PlusIcon />
              Nova Automação
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 'var(--space-4)'
        }}>
          {stats.map((stat, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-4)',
                padding: 'var(--space-5)',
                background: 'var(--glass-bg-strong)',
                backdropFilter: 'var(--glass-blur)',
                borderRadius: 'var(--radius-xl)',
                border: '1px solid var(--glass-border)'
              }}
            >
              <div style={{
                width: '52px',
                height: '52px',
                borderRadius: 'var(--radius-lg)',
                background: stat.bgColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: stat.color
              }}>
                {stat.icon}
              </div>
              <div>
                <div style={{
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--text-primary)'
                }}>
                  {stat.value}
                </div>
                <div style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--text-secondary)'
                }}>
                  {stat.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Automations List */}
      <div style={{
        background: 'var(--glass-bg-strong)',
        backdropFilter: 'var(--glass-blur)',
        borderRadius: 'var(--radius-2xl)',
        border: '1px solid var(--glass-border)',
        padding: 'var(--space-5)'
      }}>
        {loading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-16)'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              border: '3px solid var(--color-primary-200)',
              borderTopColor: 'var(--color-primary-600)',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
        ) : automations.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-16)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: 'var(--radius-xl)',
              background: 'rgba(99, 102, 241, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 'var(--space-4)',
              color: 'var(--color-primary-500)'
            }}>
              <ZapIcon />
            </div>
            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Nenhuma automação encontrada
            </h3>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
              Crie sua primeira automação clicando no botão acima
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {automations.map((automation) => {
              const statusColor = getStatusColor(automation.status)

              return (
                <div
                  key={automation.id}
                  style={{
                    padding: 'var(--space-5)',
                    borderRadius: 'var(--radius-xl)',
                    border: '1px solid var(--border-light)',
                    background: 'var(--bg-primary)',
                    display: 'flex',
                    gap: 'var(--space-5)',
                    transition: 'all var(--transition-fast)'
                  }}
                >
                  {/* Trigger Icon */}
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: 'var(--radius-xl)',
                    background: 'var(--bg-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 'var(--text-3xl)',
                    flexShrink: 0
                  }}>
                    {getTriggerIcon(automation.trigger_type)}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-2)' }}>
                      <div>
                        <h3 style={{
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--font-weight-semibold)',
                          color: 'var(--text-primary)',
                          marginBottom: 'var(--space-1)'
                        }}>
                          {automation.name}
                        </h3>
                        {automation.description && (
                          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0 }}>
                            {automation.description}
                          </p>
                        )}
                      </div>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: 'var(--radius-full)',
                        background: statusColor.bg,
                        color: statusColor.text,
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-weight-semibold)'
                      }}>
                        {statusColor.label}
                      </span>
                    </div>

                    {/* Details */}
                    <div style={{ display: 'flex', gap: 'var(--space-6)', alignItems: 'center', marginTop: 'var(--space-3)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <span style={{ fontSize: 'var(--text-xl)' }}>{getTriggerIcon(automation.trigger_type)}</span>
                        <div>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>Quando:</div>
                          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
                            {getTriggerLabel(automation.trigger_type)}
                          </div>
                        </div>
                      </div>

                      <div style={{ color: 'var(--text-tertiary)' }}>
                        <ArrowIcon />
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <span style={{ fontSize: 'var(--text-xl)' }}>{getActionIcon(automation.action_type)}</span>
                        <div>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>Então:</div>
                          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
                            {getActionLabel(automation.action_type)}
                          </div>
                        </div>
                      </div>

                      <div style={{ marginLeft: 'auto', fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
                        Criada em {new Date(automation.created_at).toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
                    <button
                      onClick={() => handleToggleStatus(automation)}
                      title={automation.status === 'active' ? 'Pausar' : 'Ativar'}
                      style={{
                        padding: 'var(--space-2)',
                        borderRadius: 'var(--radius-lg)',
                        border: '1px solid var(--border-light)',
                        background: 'var(--bg-primary)',
                        cursor: 'pointer',
                        color: 'var(--text-secondary)',
                        transition: 'all var(--transition-fast)'
                      }}
                    >
                      {automation.status === 'active' ? <PauseIcon /> : <PlayIcon />}
                    </button>
                    <button
                      onClick={() => loadLogs(automation.id)}
                      title="Ver Logs"
                      style={{
                        padding: 'var(--space-2)',
                        borderRadius: 'var(--radius-lg)',
                        border: '1px solid var(--border-light)',
                        background: 'var(--bg-primary)',
                        cursor: 'pointer',
                        color: 'var(--text-secondary)',
                        transition: 'all var(--transition-fast)'
                      }}
                    >
                      <LogsIcon />
                    </button>
                    <button
                      onClick={() => openEditModal(automation)}
                      title="Editar"
                      style={{
                        padding: 'var(--space-2)',
                        borderRadius: 'var(--radius-lg)',
                        border: '1px solid var(--border-light)',
                        background: 'var(--bg-primary)',
                        cursor: 'pointer',
                        color: 'var(--text-secondary)',
                        transition: 'all var(--transition-fast)'
                      }}
                    >
                      <EditIcon />
                    </button>
                    <button
                      onClick={() => handleDeleteAutomation(automation.id)}
                      title="Excluir"
                      style={{
                        padding: 'var(--space-2)',
                        borderRadius: 'var(--radius-lg)',
                        border: '1px solid var(--color-error-300)',
                        background: 'var(--bg-primary)',
                        cursor: 'pointer',
                        color: 'var(--color-error-500)',
                        transition: 'all var(--transition-fast)'
                      }}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={selectedAutomation ? 'Editar Automação' : 'Nova Automação'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {/* Name */}
          <div>
            <label style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', display: 'block', marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
              Nome da Automação *
            </label>
            <input
              type="text"
              value={editingAutomation.name}
              onChange={(e) => setEditingAutomation({ ...editingAutomation, name: e.target.value })}
              placeholder="Ex: Follow-up de novos leads"
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border-light)',
                fontSize: 'var(--text-sm)',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)'
              }}
            />
          </div>

          {/* Description */}
          <div>
            <label style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', display: 'block', marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
              Descrição
            </label>
            <textarea
              value={editingAutomation.description}
              onChange={(e) => setEditingAutomation({ ...editingAutomation, description: e.target.value })}
              placeholder="Descreva o que esta automação faz..."
              rows={3}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border-light)',
                fontSize: 'var(--text-sm)',
                resize: 'vertical',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)'
              }}
            />
          </div>

          {/* Trigger and Action */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <div>
              <label style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', display: 'block', marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                Quando (Gatilho) *
              </label>
              <select
                value={editingAutomation.trigger_type}
                onChange={(e) => setEditingAutomation({ ...editingAutomation, trigger_type: e.target.value })}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-light)',
                  fontSize: 'var(--text-sm)',
                  cursor: 'pointer',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
              >
                {TRIGGERS.map(trigger => (
                  <option key={trigger.value} value={trigger.value}>
                    {trigger.icon} {trigger.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', display: 'block', marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                Então (Ação) *
              </label>
              <select
                value={editingAutomation.action_type}
                onChange={(e) => setEditingAutomation({ ...editingAutomation, action_type: e.target.value })}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-light)',
                  fontSize: 'var(--text-sm)',
                  cursor: 'pointer',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
              >
                {ACTIONS.map(action => (
                  <option key={action.value} value={action.value}>
                    {action.icon} {action.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Status */}
          <div>
            <label style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', display: 'block', marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
              Status
            </label>
            <select
              value={editingAutomation.status}
              onChange={(e) => setEditingAutomation({ ...editingAutomation, status: e.target.value as any })}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border-light)',
                fontSize: 'var(--text-sm)',
                cursor: 'pointer',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)'
              }}
            >
              <option value="active">✅ Ativo</option>
              <option value="paused">⏸️ Pausado</option>
              <option value="disabled">❌ Desabilitado</option>
            </select>
          </div>

          {/* Priority */}
          <div>
            <label style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', display: 'block', marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
              Prioridade
            </label>
            <input
              type="number"
              value={editingAutomation.priority}
              onChange={(e) => setEditingAutomation({ ...editingAutomation, priority: parseInt(e.target.value) })}
              min="0"
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border-light)',
                fontSize: 'var(--text-sm)',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)'
              }}
            />
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 'var(--space-1)' }}>
              Maior número = maior prioridade de execução
            </p>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end', marginTop: 'var(--space-4)' }}>
            <button
              onClick={() => setShowModal(false)}
              style={{
                padding: 'var(--space-3) var(--space-6)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border-light)',
                background: 'var(--bg-primary)',
                cursor: 'pointer',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text-primary)'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={selectedAutomation ? handleUpdateAutomation : handleCreateAutomation}
              style={{
                padding: 'var(--space-3) var(--space-6)',
                borderRadius: 'var(--radius-lg)',
                border: 'none',
                background: 'var(--gradient-primary)',
                color: 'white',
                cursor: 'pointer',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)'
              }}
            >
              {selectedAutomation ? 'Salvar' : 'Criar Automação'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Logs Modal */}
      <Modal isOpen={showLogsModal} onClose={() => setShowLogsModal(false)} title="Logs de Execução">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {logs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--text-tertiary)' }}>
              <LogsIcon />
              <p style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-2)' }}>Nenhum log encontrado</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {logs.map((log) => (
                <div
                  key={log.id}
                  style={{
                    padding: 'var(--space-4)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--border-light)',
                    background: log.status === 'success'
                      ? 'rgba(34, 197, 94, 0.05)'
                      : log.status === 'failed'
                      ? 'rgba(239, 68, 68, 0.05)'
                      : 'rgba(245, 158, 11, 0.05)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                    <span style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: log.status === 'success'
                        ? 'var(--color-success-600)'
                        : log.status === 'failed'
                        ? 'var(--color-error-600)'
                        : 'var(--color-warning-600)'
                    }}>
                      {log.status === 'success' ? '✅ Sucesso' : log.status === 'failed' ? '❌ Falha' : '⏳ Pendente'}
                    </span>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                      {new Date(log.created_at).toLocaleString('pt-BR')}
                    </span>
                  </div>
                  {log.error_message && (
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error-600)', marginTop: 'var(--space-2)' }}>
                      <strong>Erro:</strong> {log.error_message}
                    </div>
                  )}
                  <details style={{ marginTop: 'var(--space-2)' }}>
                    <summary style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', cursor: 'pointer', fontWeight: 'var(--font-weight-medium)' }}>
                      Ver detalhes
                    </summary>
                    <pre style={{
                      marginTop: 'var(--space-2)',
                      padding: 'var(--space-2)',
                      background: 'var(--bg-tertiary)',
                      borderRadius: 'var(--radius-md)',
                      overflow: 'auto',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-secondary)'
                    }}>
                      {JSON.stringify({ trigger_data: log.trigger_data, action_data: log.action_data }, null, 2)}
                    </pre>
                  </details>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Flowchart Editor Modal */}
      {showFlowEditor && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10000
        }}>
          <FlowchartEditor
            onSave={(nodes, edges) => {
              console.log('Fluxo salvo:', { nodes, edges })
              setShowFlowEditor(false)
            }}
            onClose={() => setShowFlowEditor(false)}
          />
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}

export default Automations
